import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDBadge from "components/MDBadge";
import DataTable from "examples/Tables/DataTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";
import Swal from "sweetalert2";

function SupportTypeBadge({ isForCourse, isForExam }) {
  const content = isForCourse ? "Course" : isForExam ? "Exam" : "General";
  const color = isForCourse ? "success" : isForExam ? "info" : "warning";
  return <MDBadge variant="contained" color={color} badgeContent={content} />;
}

SupportTypeBadge.propTypes = {
  isForCourse: PropTypes.bool.isRequired,
  isForExam: PropTypes.bool.isRequired,
};

function ElectronicSupportsPage() {
  const navigate = useNavigate();
  const [supports, setSupports] = useState([]);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/api/V2/slectronicsupport/all`)
      .then((response) => {
        const fetchedSupports = response.data.electronicSupports;
        setSupports(fetchedSupports);
      })
      .catch((error) => {
        console.error("There was an error fetching the electronic supports data!", error);
      });
  }, []);

  const deleteOneSupport = async (id) => {
    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "This support will be permanently deleted!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      });

      if (result.isConfirmed) {
        await axios.delete(
          `${process.env.REACT_APP_API_URL}/api/V2/slectronicsupport/delete/${id}`
        );
        setSupports((prev) => prev.filter((support) => support._id !== id));
        Swal.fire("Deleted!", "The support has been deleted.", "success");
      }
    } catch (error) {
      console.error("There was an error deleting the support!", error);
      Swal.fire("Error!", "Failed to delete the support. Please try again.", "error");
    }
  };

  const supportsTableData = {
    columns: [
      { Header: "Title", accessor: "title", align: "left" },
      { Header: "File URL", accessor: "fileUrl", align: "left" },
      { Header: "Type", accessor: "type", align: "center" },
      { Header: "Actions", accessor: "actions", align: "center" },
    ],
    rows: supports.map((support) => ({
      title: support.title,
      fileUrl: (
        <a href={support.cv.url} target="_blank" rel="noopener noreferrer">
          View File
        </a>
      ),
      type: <SupportTypeBadge isForCourse={support.isForCourse} isForExam={support.isForExam} />,
      actions: (
        <MDBox display="flex" justifyContent="center" gap={1}>
          <Tooltip title="Delete Support">
            <IconButton color="error" onClick={() => deleteOneSupport(support._id)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </MDBox>
      ),
    })),
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox
        mx={2}
        mt={-1}
        py={3}
        px={2}
        variant="gradient"
        bgColor="info"
        borderRadius="lg"
        coloredShadow="info"
      >
        <MDTypography variant="h6" color="white">
          All Electronic Supports
        </MDTypography>
      </MDBox>
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox pt={3}>
                <DataTable
                  table={supportsTableData}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default ElectronicSupportsPage;
